import type { PopperPlacementType } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import type React from 'react';
import type { ActionItem, MenuGroup } from 'src/constants';
import { MENU_GROUP_ORDER, MENU_GROUPS } from 'src/constants';

/** Minimum items required before group headers are shown */
const AUTO_GROUP_THRESHOLD = 4;

export interface StyledContextMenuProps {
  actions: ActionItem[];
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onAction: (action: ActionItem, index: number) => void;
  dangerLabels?: string[];
  placement?: PopperPlacementType;
  testIdPrefix?: string;
  id?: string;
  headerContent?: React.ReactNode;
}

// && doubles class specificity to beat MUI's default MenuItem/Paper styles
const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    '&&': {
      width: 190,
      borderRadius: 10,
      padding: 6,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`,
    },
  },
  groupHeader: {
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: 0.8,
    textTransform: 'uppercase' as const,
    color: theme.palette.text.disabled,
    padding: '6px 10px 2px',
    lineHeight: 'normal',
  },
  menuItem: {
    '&&': {
      fontSize: 12,
      fontWeight: 500,
      padding: '7px 10px',
      gap: 10,
      borderRadius: 6,
      color: theme.palette.text.secondary,
      minHeight: 'unset',
      lineHeight: 'normal',
      transition: 'background-color 120ms ease, color 120ms ease',
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.08),
      color: theme.palette.text.primary,
    },
    '&:hover $iconWrapper': {
      color: theme.palette.secondary.main,
    },
  },
  menuItemDanger: {
    '&&': {
      color: theme.palette.error.main,
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.error.main, 0.1),
      color: theme.palette.error.main,
    },
    '&:hover $iconWrapper': {
      color: theme.palette.error.main,
    },
  },
  iconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: 'inherit',
    transition: 'color 120ms ease',
    '& > *': {
      fontSize: '16px !important',
      width: 16,
      height: 16,
    },
  },
  divider: {
    height: 1,
    backgroundColor: theme.palette.divider,
    margin: '4px 8px',
  },
  headerContent: {
    padding: '8px 10px 4px',
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: 4,
  },
  menuList: {
    '&&': {
      padding: 0,
    },
  },
}));

/**
 * Determines whether to show group headers based on item-level `group` metadata.
 * Shows groups when 4+ items span 2+ distinct groups, or when any Danger Zone
 * item is present (to always visually separate destructive actions).
 */
function shouldAutoGroup(actions: ActionItem[]): boolean {
  const groups = new Set(actions.map((a) => a.group).filter(Boolean));
  if (groups.has(MENU_GROUPS.DANGER) && groups.size >= 2) return true;
  if (actions.length < AUTO_GROUP_THRESHOLD) return false;
  return groups.size >= 2;
}

/** Sorts actions into ordered groups based on MENU_GROUP_ORDER */
function buildGroups(actions: ActionItem[]): { groupName: MenuGroup; items: ActionItem[] }[] {
  const grouped = new Map<MenuGroup, ActionItem[]>();

  for (const action of actions) {
    if (action.group) {
      const existing = grouped.get(action.group) || [];
      existing.push(action);
      grouped.set(action.group, existing);
    }
  }

  // Sort groups by the canonical MENU_GROUP_ORDER
  const ordered: { groupName: MenuGroup; items: ActionItem[] }[] = [];
  for (const groupName of MENU_GROUP_ORDER) {
    const items = grouped.get(groupName);
    if (items && items.length > 0) {
      ordered.push({ groupName, items });
    }
  }

  // Append any actions with unrecognized group names at the end
  for (const [groupName, items] of grouped) {
    if (!MENU_GROUP_ORDER.includes(groupName)) {
      ordered.push({ groupName, items });
    }
  }

  return ordered;
}

export const StyledContextMenu: React.FC<StyledContextMenuProps> = ({
  actions,
  anchorEl,
  open,
  onClose,
  onAction,
  dangerLabels = [],
  placement = 'bottom-start',
  testIdPrefix,
  id,
  headerContent,
}) => {
  const classes = useStyles();
  const useGrouped = shouldAutoGroup(actions);

  const isDanger = (action: ActionItem): boolean => {
    if (action.group === MENU_GROUPS.DANGER) return true;
    return dangerLabels.includes(action.label);
  };

  const getTestId = (label: string): string | undefined => {
    if (!testIdPrefix) return undefined;
    const slug = label.toLowerCase().replace(/\s+/g, '-');
    return `${testIdPrefix}-${slug}`;
  };

  const findActionIndex = (label: string): number => actions.findIndex((a) => a.label === label);

  const renderItem = (action: ActionItem) => {
    const danger = isDanger(action);
    const originalIndex = findActionIndex(action.label);

    return (
      <MenuItem
        key={action.label}
        className={`${classes.menuItem} ${danger ? classes.menuItemDanger : ''}`}
        disabled={action.disabled}
        onClick={() => onAction(action, originalIndex)}
        id={getTestId(action.label)}
      >
        <span className={classes.iconWrapper}>{action.icon}</span>
        {action.label}
      </MenuItem>
    );
  };

  const renderGrouped = () => {
    const groups = buildGroups(actions);
    const ungrouped = actions.filter((a) => !a.group);
    const elements: React.ReactNode[] = [];

    groups.forEach(({ groupName, items }, groupIndex) => {
      if (groupIndex > 0) {
        elements.push(<div key={`divider-${groupName}`} className={classes.divider} />);
      }

      elements.push(
        <Typography key={`header-${groupName}`} className={classes.groupHeader}>
          {groupName}
        </Typography>,
      );

      for (const action of items) {
        elements.push(renderItem(action));
      }
    });

    if (ungrouped.length > 0) {
      if (elements.length > 0) {
        elements.push(<div key="divider-ungrouped" className={classes.divider} />);
      }
      for (const action of ungrouped) {
        elements.push(renderItem(action));
      }
    }

    return elements;
  };

  const renderFlat = () => actions.map((action) => renderItem(action));

  return (
    <Popper open={open} anchorEl={anchorEl} placement={placement} transition style={{ zIndex: 2000 }}>
      {({ TransitionProps }) => (
        <Grow {...TransitionProps}>
          <Paper className={classes.paper} elevation={0}>
            <ClickAwayListener onClickAway={onClose}>
              <div>
                {headerContent && <div className={classes.headerContent}>{headerContent}</div>}
                <MenuList className={classes.menuList} autoFocusItem={open} id={id}>
                  {useGrouped ? renderGrouped() : renderFlat()}
                </MenuList>
              </div>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

export default StyledContextMenu;
